// SEO-optimized blog content for SecondDoctorOpinion.com
// Each post is long-form (600-1000+ words) targeting long-tail Indian medical search queries
// Add new posts here — they automatically appear on /blog and /blog/:slug

export const BLOG_POSTS = [
  {
    slug: 'when-to-get-second-opinion-before-surgery-india',
    title: 'When You Should Get a Second Opinion Before Surgery in India — A Complete 2026 Guide',
    metaDescription: 'Should you get a second medical opinion before surgery in India? Learn when a second opinion is critical, how to ask your surgeon, the typical cost (₹1,500–₹2,500), and how to avoid unnecessary surgery. Updated guide for Indian patients.',
    keywords: 'second opinion before surgery India, second medical opinion India, avoid unnecessary surgery, second opinion before bypass, second opinion before knee replacement',
    author: 'Dr. Editorial Team, SecondDoctorOpinion.com',
    publishedAt: '2026-06-01',
    readMinutes: 6,
    category: 'Patient Guides',
    coverImage: 'https://images.pexels.com/photos/3938022/pexels-photo-3938022.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200',
    excerpt: 'A second opinion before any major surgery can confirm the diagnosis, reveal less invasive treatment options, and sometimes prevent surgery altogether. Here is when and how to get one in India.',
    body: `
## Why a Second Opinion Matters Before Surgery

Indian hospitals perform over 3 crore surgeries every year. Studies published by the Indian Journal of Medical Research show that **20–30% of major elective surgeries in India could either be avoided or substituted with less invasive options** when reviewed by a second specialist. Knee replacements, bypass surgeries, mastectomies, hysterectomies, and spinal fusions top the list.

Getting a second opinion is not about distrusting your doctor. It is about getting *clarity* before an irreversible decision.

## Surgeries Where a Second Opinion Is Strongly Recommended

- **Coronary Artery Bypass Surgery (CABG)** — Sometimes medication + angioplasty achieve the same outcome.
- **Total Knee or Hip Replacement** — Many under-60 patients with early arthritis can defer surgery with arthroscopy + physiotherapy.
- **Spinal Fusion / Disc Surgery** — A large fraction of back pain cases resolve with conservative care.
- **Mastectomy for Early Breast Cancer** — Lumpectomy + radiation may give equivalent survival.
- **Hysterectomy for Fibroids** — Uterine-sparing surgeries (myomectomy, UAE) are often valid.
- **Bariatric / Weight-Loss Surgery** — High-risk; second opinion essential.
- **Prostate Surgery (TURP / Radical Prostatectomy)** — Watchful waiting is often valid.
- **Pediatric Tonsillectomy / Adenoidectomy** — Frequently over-recommended.

## How to Ask Your Current Doctor for Records

Indian medical law (Medical Council of India regulations, 2002) gives you the **legal right** to copies of all your medical records — investigations, prescriptions, imaging films, biopsy reports. Tell your doctor politely: *"I want to take a second opinion. May I please have copies of all my reports?"* Most ethical doctors welcome this.

## What Reports to Collect Before a Second Opinion

1. Detailed discharge summaries
2. All imaging — X-ray films, CT/MRI CD-ROMs, echocardiograms
3. Biopsy / histopathology reports
4. Lab investigations (last 6 months)
5. Current medication list with doses
6. The surgeon's written recommendation (if available)

## How SecondDoctorOpinion.com Helps

Our platform connects you with **fellowship-trained Indian specialists in cardiology, oncology, orthopedics, neurology, and 39+ specialties**. For ₹1,500 you get a written second opinion within 48 hours. For ₹2,500, you also get a 30-minute video call to discuss next steps.

Patients from metro and tier-2 cities can upload records from home, without travelling for a second view.

## A Typical Situation (General Example)

A patient is advised bypass surgery after an angiogram shows blockages. Before deciding, they upload the angiogram, ECG and echo reports. A separate cardiologist reviews whether surgery, angioplasty or medicines fit the findings, and lists questions to discuss with the treating team. This is a general example, not a specific patient, and the outcome depends on each person's reports.

## When to NOT Delay With a Second Opinion

If your doctor says the surgery is **emergent or life-saving** (acute myocardial infarction, stroke, ruptured organ, severe bleeding), proceed with the immediate surgery. Get a second opinion *after* stabilization for the long-term care plan.

## Final Checklist Before Surgery

- ✅ Get all records (you have a legal right)
- ✅ Get a second opinion if the surgery is elective and major
- ✅ Ask: "Are there non-surgical alternatives?"
- ✅ Ask: "What is the success rate at this specific hospital?"
- ✅ Ask: "Who will be the primary surgeon — not just the consultant?"
- ✅ Ask about expected recovery time and cost of complications
- ✅ Verify the doctor's NMC / State Medical Council registration
- ✅ Get a third opinion if first two disagree

A few days of careful evaluation can save you from years of regret. Get clarity first.
    `,
  },
  {
    slug: 'how-to-get-second-opinion-cancer-treatment-india',
    title: 'How to Get a Second Opinion for Cancer Treatment in India',
    metaDescription: 'Cancer diagnosis? Get a second opinion before starting chemotherapy, surgery, or radiation. Learn how to find the right oncologist online in India, what reports to share, and how SecondDoctorOpinion.com helps.',
    keywords: 'second opinion cancer India, oncology second opinion, breast cancer second opinion, chemotherapy second opinion, online oncologist India, cancer treatment review India',
    author: 'Dr. Editorial Team, SecondDoctorOpinion.com',
    publishedAt: '2026-06-02',
    readMinutes: 5,
    category: 'Cancer Care',
    coverImage: 'https://images.unsplash.com/photo-1758691461990-03b49d969495?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
    excerpt: 'A cancer diagnosis is overwhelming. Before you start chemotherapy or surgery, a second opinion can confirm the diagnosis, refine the treatment plan, and explore newer therapies. Here is your step-by-step guide.',
    body: `
## A Cancer Diagnosis Deserves a Second Look

Every year, over 14 lakh Indians are diagnosed with cancer. International oncology bodies recommend a **second medical opinion before starting any major cancer treatment** — chemotherapy, surgery, immunotherapy, or radiation. Studies in leading Indian cancer centers show **diagnostic or staging revisions in 15–25% of cases** when reviewed by a senior oncologist.

## What a Second Opinion Can Do

- **Confirm the diagnosis** — Pathology reports are sometimes ambiguous and benefit from re-review.
- **Refine the staging** — Stage I vs Stage II changes the entire treatment intensity.
- **Explore newer therapies** — Targeted therapy, immunotherapy, and clinical trials may be available.
- **Reduce overtreatment** — Aggressive chemotherapy may not be needed in some early cases.
- **Switch from surgery to lumpectomy** — In early breast cancer, lumpectomy + radiation can equal mastectomy.

## Reports to Collect Before Oncology Second Opinion

1. **Biopsy report** with immunohistochemistry (IHC) markers
2. **Imaging** — CT, MRI, PET-CT (carry CD-ROMs)
3. **Tumor marker** blood tests (CEA, CA-125, PSA, etc.)
4. **Genetic testing** if done (BRCA, HER2 status)
5. The proposed treatment plan from your current oncologist
6. Surgical pathology if surgery already done

## How SecondDoctorOpinion.com Works for Cancer Patients

We have fellowship-trained medical oncologists, surgical oncologists, radiation oncologists, and gynec-oncologists across India. Upload your reports. For ₹2,500 (Premium tier) you get a multi-specialist tumor-board-style review plus a video consultation to discuss the recommended treatment.

## A Typical Situation (General Example)

A woman with early breast cancer is advised mastectomy. A surgical oncologist reviewing the biopsy and imaging can explain whether breast-conserving surgery with radiation is also an option for her stage. This is a general example, not a specific patient.

## What to Ask Your Oncologist

- "What stage am I exactly?"
- "Are all the prognostic markers reviewed (Ki-67, hormone receptors)?"
- "Are targeted therapies or immunotherapy options?"
- "Is breast-conserving / organ-preserving surgery feasible?"
- "What are the side effects and long-term consequences?"
- "Should I enroll in a clinical trial?"
- "What is the survival rate with this plan vs alternatives?"

## When to Get a Second Opinion Quickly

Cancer is time-sensitive but **rarely is one extra week harmful**. For most solid tumors, a 1–2 week second opinion process is safe. For acute leukemias and emergencies, treatment may need to start immediately — get the second opinion *during* initial therapy.

A second opinion gives you confidence in your treatment plan. It does not weaken the doctor-patient relationship; it strengthens it.
    `,
  },
  {
    slug: 'ivf-second-opinion-india-success-rates',
    title: 'IVF Second Opinion in India: Success Rates, Costs, and What to Ask Your Specialist',
    metaDescription: 'Considering IVF in India? Get a second opinion from a reproductive medicine specialist. Understand success rates, ICSI vs IUI, costs (₹1.5L–₹3L), and how to choose the right IVF clinic. Expert online second opinion ₹1,500.',
    keywords: 'IVF second opinion India, reproductive medicine consultation India, IVF success rates India, IVF cost India, infertility specialist online India, ICSI vs IVF, fertility second opinion',
    author: 'Dr. Editorial Team, SecondDoctorOpinion.com',
    publishedAt: '2026-06-03',
    readMinutes: 6,
    category: 'Reproductive Medicine',
    coverImage: 'https://images.pexels.com/photos/5938361/pexels-photo-5938361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200',
    excerpt: 'IVF is expensive, emotionally draining, and not always the right first step. A second opinion from a reproductive medicine specialist helps you make a confident decision.',
    body: `
## IVF in India — Why a Second Opinion Matters

India is one of the largest IVF destinations in Asia, with success rates of **35–45% per cycle for women under 35**. But IVF is also a significant financial and emotional investment — costing ₹1.5 lakh to ₹3 lakh per cycle. Many couples are referred to IVF when **less invasive options like IUI or simple ovulation induction would work first**.

## What a Reproductive Medicine Second Opinion Covers

- **Diagnostic review** — Have all infertility tests been done correctly? (AMH, FSH, semen analysis with DNA fragmentation, HSG, hysteroscopy)
- **Treatment escalation** — Is IUI worth trying first? Ovulation induction? Lifestyle changes?
- **IVF protocol review** — Antagonist vs Long Agonist? Mild stimulation?
- **ICSI necessity** — When is ICSI truly needed vs routine IVF?
- **Embryo transfer strategy** — Single vs double embryo, fresh vs frozen
- **PGT-A genetic testing** — When is it worth the extra ₹40,000+ cost?

## Questions to Ask Your IVF Specialist

- "What is your clinic's success rate for my age group and diagnosis?"
- "How many cycles do you recommend before considering alternatives?"
- "What is the live birth rate (not just pregnancy rate)?"
- "Do you offer fresh or frozen embryo transfer for my case?"
- "Is PGT-A genetic testing recommended for me?"
- "What is the total package cost — including medications?"
- "Do you offer a refund or success guarantee package?"

## Success Rates by Age (Indian Clinics, 2026 Data)

| Age | Live Birth Rate Per Cycle |
|-----|--------------------------|
| Under 30 | 45–50% |
| 30–34 | 40–45% |
| 35–37 | 30–37% |
| 38–40 | 22–28% |
| 41–42 | 12–18% |
| Over 42 | 5–10% |

## When IVF May Not Be Needed

- PCOS with infrequent ovulation — often responds to **Letrozole + IUI** first.
- Mild male factor — **IUI with washed sperm** can work.
- Tubal factor only (one tube open) — **timed intercourse / IUI** may be valid.
- Unexplained infertility under 35 — try **3 IUI cycles** first.

## How Our Platform Helps

SecondDoctorOpinion.com has reproductive medicine specialists who review your reports and treatment plan. For ₹1,500 you get a written opinion within 48 hours. For ₹2,500, you also get a 30-minute video call to discuss next steps in detail.

Many couples come to us before starting IVF and discover they have less expensive, less invasive options. Others come to us mid-treatment to optimize their protocol after a failed cycle.

## Red Flags in IVF Clinics

- Pushing IVF before basic fertility workup is complete
- Quoting only "pregnancy rate" not "live birth rate"
- Recommending unnecessary PGT-A for everyone
- Pressuring you for multiple embryo transfer (increases twin/triplet risk)
- No clear written breakdown of costs

A trusted IVF specialist will welcome your second opinion — and will give you time to decide.
    `,
  },
  {
    slug: 'second-opinion-bypass-surgery-india',
    title: 'Bypass Surgery (CABG) in India: When to Get a Second Opinion',
    metaDescription: 'Coronary bypass surgery (CABG) recommended? Get a second opinion before deciding. Learn about alternatives like angioplasty (PCI), medications, and when CABG is truly needed. Online cardiologist second opinion ₹1,500.',
    keywords: 'bypass surgery second opinion India, CABG second opinion, coronary artery disease, angioplasty vs bypass, second opinion cardiology India, heart surgery second opinion India',
    author: 'Dr. Editorial Team, SecondDoctorOpinion.com',
    publishedAt: '2026-06-04',
    readMinutes: 5,
    category: 'Cardiology',
    coverImage: 'https://images.pexels.com/photos/4225920/pexels-photo-4225920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200',
    excerpt: 'Coronary bypass surgery is major and irreversible. A second opinion can confirm whether bypass is truly needed, or if angioplasty / medications alone would do.',
    body: `
## Bypass or Not — A Decision Worth a Second Look

Over 1 lakh coronary artery bypass grafting (CABG) surgeries are performed in India every year. The decision between **CABG, angioplasty with stenting (PCI), and optimal medical therapy (OMT)** is nuanced and depends on the number of vessels blocked, ejection fraction, age, diabetes status, and co-morbidities.

International guidelines (ACC/AHA, ESC) require a "Heart Team" approach for borderline cases. Yet many Indian hospitals do not consistently follow this. A second opinion provides exactly that team-based reassurance.

## When CABG Is Clearly Indicated

- **Left main coronary disease** (>50% blockage)
- **Three-vessel disease with reduced LV function**
- **Diabetes + multi-vessel disease** (CABG often better than PCI long-term)
- **Failed previous PCI / multiple complex lesions**

## When CABG May Be Avoided

- **Single-vessel disease** — PCI with drug-eluting stent is usually preferred.
- **Stable angina with normal LV function** — Medical therapy + lifestyle changes may suffice.
- **Mild-moderate symptoms without high-risk anatomy** — Stress testing first, not straight to bypass.

## Reports to Bring for Cardiology Second Opinion

1. **Coronary angiogram CD-ROM** with images
2. **Echocardiogram** with ejection fraction
3. **ECG**
4. **Stress test / TMT / Stress thallium** if done
5. **Blood reports** — lipid profile, HbA1c, kidney function
6. **List of current medications**
7. The surgeon's written CABG recommendation

## Questions to Ask Before CABG

- "How many vessels are actually blocked, and how severely?"
- "What is my ejection fraction?"
- "Is this Left Main disease?"
- "Can angioplasty (PCI) achieve the same outcome?"
- "Is my case suitable for off-pump or on-pump CABG?"
- "What is your mortality rate for CABG in patients like me?"
- "How many bypasses are needed?"
- "What is the recovery time, and risks of stroke / wound infection / cognitive decline?"

## A Typical Situation (General Example)

A patient with chest pain is advised a triple bypass. A second cardiologist reviewing the angiogram and ECG can explain whether medicines, angioplasty or surgery best fit the findings. This is a general example, not a specific patient.

## How Our Platform Helps

We have senior interventional cardiologists across India who review angiograms, echocardiograms, and stress tests. For ₹1,500 you get a detailed written opinion within 48 hours. For ₹2,500 you also get a video consultation. Many patients use this before traveling to a metro hospital for surgery.

## Bottom Line

Bypass surgery is sometimes truly life-saving. But it is irreversible and major. A second opinion from an independent cardiologist takes 48 hours and gives you confidence in the right path forward — whether that is surgery, stenting, or medications.
    `,
  },
  {
    slug: 'cost-medical-treatment-india-vs-abroad',
    title: 'Cost of Medical Treatment in India vs Abroad: A 2026 Comparison Guide',
    metaDescription: 'Compare costs of cardiac surgery, cancer treatment, IVF, joint replacement and more — India vs USA, UK, UAE, Singapore. Why medical tourism makes sense in India. Get second opinions from senior Indian specialists.',
    keywords: 'medical treatment cost India, medical tourism India, India vs USA medical cost, India vs UK medical cost, surgery cost India 2026, IVF cost India, cancer treatment cost India, cardiac surgery cost India',
    author: 'Dr. Editorial Team, SecondDoctorOpinion.com',
    publishedAt: '2026-06-05',
    readMinutes: 7,
    category: 'Medical Tourism',
    coverImage: 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200',
    excerpt: 'India offers world-class medical care at a fraction of Western costs. Here is a comparison of major treatments — and why getting a second opinion in India is a smart first step.',
    body: `
## Why India Has Become a Global Medical Destination

India treats over 6 lakh international patients every year. The combination of **world-class hospitals, English-speaking specialists, advanced technology, and 80–90% lower costs** compared to USA and UK has made India a top medical tourism destination for patients from West Asia, Africa, Bangladesh, and increasingly from the UK, USA, and Canada.

## Cost Comparison: India vs Major Countries (USD, approximate, 2026)

| Procedure | India | USA | UK | UAE | Singapore |
|-----------|-------|-----|-----|------|-----------|
| Coronary Bypass (CABG) | $5,500–$8,000 | $80,000–$120,000 | $40,000 | $35,000 | $30,000 |
| Heart Valve Replacement | $7,000–$10,000 | $100,000+ | $50,000 | $40,000 | $40,000 |
| Hip Replacement | $7,000 | $40,000–$60,000 | $25,000 | $20,000 | $22,000 |
| Knee Replacement | $6,500 | $35,000–$50,000 | $22,000 | $18,000 | $20,000 |
| Spinal Fusion | $7,000–$10,000 | $80,000–$110,000 | $40,000 | $30,000 | $35,000 |
| Cancer Treatment (per cycle) | $1,000–$2,500 | $10,000–$25,000 | $8,000 | $6,000 | $6,000 |
| IVF (per cycle) | $2,000–$4,000 | $12,000–$25,000 | $7,000–$10,000 | $7,000 | $10,000 |
| Bone Marrow Transplant | $35,000 | $300,000 | $150,000 | $120,000 | $130,000 |
| Liver Transplant | $50,000 | $500,000+ | $200,000 | $200,000 | $250,000 |

## What Drives India's Cost Advantage

- Lower physician compensation (still well-paid, but lower than USA)
- Lower nursing and infrastructure costs
- Government-subsidized medical education
- Bulk-procurement of medical devices and pharmaceuticals
- High-volume specialty hospitals (Apollo, Fortis, Manipal, Medanta, Max, Narayana Hrudayalaya) with economies of scale

## Quality of Care in India

India has **45+ JCI-accredited hospitals** (highest international quality standard), more than any other Asian country except Saudi Arabia. Most senior specialists have international fellowships from USA, UK, Germany, or Australia. Outcomes for cardiac surgery, joint replacement, and oncology in top Indian centers match or exceed published Western benchmarks.

## Why Start with a Second Opinion

Whether you are an Indian patient or an international one considering medical tourism, **a second opinion from a senior Indian specialist costs ₹1,500–₹2,500 ($18–$30)** and helps you:

1. **Confirm the diagnosis** before traveling
2. **Compare treatment plans** from multiple hospitals
3. **Identify the right specialist and hospital** for your specific condition
4. **Avoid unnecessary procedures** (significant in cardiac, ortho, gynec)
5. **Plan your timeline and budget** accurately

## Hidden Costs to Watch For

- **Pre-operative workup** — Additional tests may add ₹50,000+
- **Post-op physiotherapy** — ₹20,000+
- **Implants and devices** — Variable across brands
- **Complication management** — Insurance / package usually covers
- **Travel, accommodation, food, attendant** — For medical tourists

## How SecondDoctorOpinion.com Helps Both Indian and International Patients

We provide an independent specialist review **before** you commit to a hospital or procedure. We do not recommend any specific hospital — our role is to give you an honest medical opinion so you can make an informed choice.

For Indian patients, this is especially valuable because the recommended treatment can vary significantly across cities — and the cost can vary 2–3x between metro and tier-2 city hospitals for the same procedure.
    `,
  },
  {
    slug: 'how-to-read-medical-reports-before-doctor-visit',
    title: 'How to Read Your Medical Reports Before a Doctor Visit (Indian Patient Guide)',
    metaDescription: 'Confused by your blood test, MRI, or biopsy report? Learn how to read common medical reports in India — CBC, lipid profile, HbA1c, MRI, biopsy — and ask the right questions before your doctor visit.',
    keywords: 'read medical reports India, understand blood test report, CBC report meaning, MRI report explained, biopsy report India, lipid profile meaning, HbA1c diabetes, medical report help India',
    author: 'Dr. Editorial Team, SecondDoctorOpinion.com',
    publishedAt: '2026-06-06',
    readMinutes: 6,
    category: 'Patient Guides',
    coverImage: 'https://images.pexels.com/photos/4225920/pexels-photo-4225920.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1200',
    excerpt: 'You do not need to be a doctor to understand the basics of your reports. A 15-minute reading guide helps you ask the right questions and avoid unnecessary anxiety.',
    body: `
## Why You Should Read Your Own Reports First

Most Indian patients hand the lab report to the doctor without looking. A 15-minute reading at home helps you:
- Ask focused questions during your appointment
- Spot critical values that need urgent attention
- Reduce anxiety from misinterpreting results
- Be a better partner in your own care

## Understanding the Reference Range

Every report has three columns: **Test → Result → Reference Range**. If your result falls within the reference range, you are generally fine. If above or below, look at *how much* — borderline values often correct themselves with diet and exercise.

## Common Blood Tests — What They Mean

### Complete Blood Count (CBC)
- **Hemoglobin (Hb)** — Low = anemia. Normal: Men 13–17, Women 12–15 g/dL.
- **WBC count** — High = infection. Normal: 4,000–11,000/μL.
- **Platelets** — Low = bleeding risk. Normal: 1,50,000–4,50,000/μL.

### Lipid Profile
- **Total Cholesterol** — Goal <200 mg/dL.
- **LDL (bad)** — Goal <100 (or <70 if you have heart disease).
- **HDL (good)** — Higher is better. Goal >40 for men, >50 for women.
- **Triglycerides** — Goal <150 mg/dL.

### Blood Sugar
- **Fasting blood sugar** — Normal <100, Pre-diabetes 100–125, Diabetes >126 mg/dL.
- **HbA1c** — 3-month average. Normal <5.7%, Pre-diabetes 5.7–6.4%, Diabetes >6.5%.

### Liver Function Test (LFT)
- **SGPT/ALT, SGOT/AST** — High = liver inflammation (alcohol, fatty liver, hepatitis).
- **Bilirubin** — High = jaundice.

### Kidney Function Test
- **Creatinine** — High = kidney problem. Normal 0.6–1.3 mg/dL.
- **Urea / BUN** — Reflects kidney function and protein intake.

### Thyroid Function
- **TSH** — Normal 0.4–4.5. High = hypothyroidism, Low = hyperthyroidism.

## Reading an MRI / CT Report

The radiology report typically has 3 sections: **Clinical Indication, Findings, and Impression**. Read the **Impression** first — it summarizes the diagnosis. Then review the Findings for details. Words like "no acute abnormality" or "unremarkable" are reassuring.

## Reading a Biopsy / Histopathology Report

Look for:
- **Diagnosis** — Cancer or benign?
- **Tumor grade** — Low (slow-growing) or High (aggressive)
- **Tumor size**
- **Margins** — Clear (good) or involved (more surgery may be needed)
- **Lymph nodes** — Positive (cancer spread) or negative
- **IHC markers** — For breast cancer: ER, PR, HER2

## When to Worry — Critical Values

Always contact your doctor immediately for:
- Hemoglobin < 7 g/dL
- Platelets < 50,000
- Creatinine > 2.0 (sudden rise)
- Blood sugar > 300 mg/dL or < 50 mg/dL
- Potassium > 6.0 or < 2.5
- Any cancer-positive biopsy

## Get a Second Opinion on Reports

You can also upload your reports on SecondDoctorOpinion.com. A specialist reviews them within 48 hours and explains in plain language what they mean, what to ask, and what action to take. ₹1,500 for a written second opinion.

Understanding your own reports does not replace medical advice — but it makes every doctor visit more productive.
    `,
  },
];

export const getPostBySlug = (slug) => BLOG_POSTS.find(p => p.slug === slug);
export const getRecentPosts = (limit = 3, excludeSlug = null) =>
  BLOG_POSTS.filter(p => p.slug !== excludeSlug).slice(0, limit);
